/**
 * Nové tipy – návrhy nových písní pro repertoár.
 * Každý uživatel může dát 1–5 hvězdiček (1 = nehrát, 5 = určitě hrát).
 * Pořadí = součet hvězdiček od všech hlasujících, sestupně.
 *
 * Tip má stav (sloupec song_tips.status):
 *   active   – hlasuje se o něm (výchozí)
 *   wip      – na zkoušku: pracuje se na zařazení do repertoáru
 *   rejected – zamítnutá
 * Archivovat smí jen správce (vynuceno triggerem v DB, viz migrace 24).
 * Píseň „na zkoušku" zároveň dostane bod na Zkoušce (rehearsal_items.tip_id).
 */
import { supabase } from '../lib/supabase.js';
import { isAdmin } from '../lib/auth.js';

const ACTIVE = 'active';
const WIP = 'wip';
const REJECTED = 'rejected';

const STATUS_LABEL = {
    [WIP]: 'na zkoušku',
    [REJECTED]: 'zamítnutá'
};

// Které sekce archivu má uživatel rozbalené – přežije překreslení seznamu
const openArchive = new Set();

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function fmtDate(d) {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`;
}

// České skloňování: 0 a 5+ = genitiv plurálu ("0 hlasů", ne "0 hlasy")
function plural(n, one, few, many) {
    if (n === 1) return one;
    return (n >= 2 && n <= 4) ? few : many;
}

function voterWord(n) {
    return plural(n, 'hlas', 'hlasy', 'hlasů');
}

// Stav tipu odolně vůči tomu, že migrace 24 ještě neproběhla (sloupec chybí).
// Neznámý stav schováme do archivu — nikdy ho nevracíme mezi aktivní,
// aby se archivovaný tip po budoucí změně schématu tiše neobjevil zpátky.
function tipStatus(t) {
    const s = t.status || ACTIVE;
    if (s === ACTIVE || s === WIP || s === REJECTED) return s;
    return REJECTED;
}

// Odkaz na YouTube smí být jen http(s) — editovat tip může kdokoli,
// takže hodnotu bereme jako nedůvěryhodnou (javascript:, data: apod.).
function safeUrl(url) {
    const u = String(url || '').trim();
    return /^https?:\/\//i.test(u) ? u : null;
}

// Hláška, když v DB chybí sloupec z migrace 24
function migrationHint(error) {
    const code = error?.code || '';
    if (code === '42703' || code === 'PGRST204' || /column .* does not exist/i.test(error?.message || '')) {
        return ' — vypadá to, že v Supabase ještě neproběhla migrace 24-song-tips-archive.sql.';
    }
    return '';
}

export async function renderTipy(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
        container.innerHTML = '<div class="state-banner">Nejdřív se přihlas.</div>';
        return;
    }

    const [tipsRes, votesRes, profilesRes, admin] = await Promise.all([
        supabase.from('song_tips').select('*').order('created_at', { ascending: false }),
        supabase.from('song_tip_votes').select('*'),
        supabase.from('profiles').select('id, name'),
        isAdmin()
    ]);

    if (tipsRes.error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(tipsRes.error.message)}</div>`;
        return;
    }

    const tips = tipsRes.data || [];
    const votes = votesRes.data || [];
    const profilesMap = new Map((profilesRes.data || []).map((p) => [p.id, p.name]));

    // Spočítej souhrny per tip
    const votesByTip = new Map();
    votes.forEach((v) => {
        if (!votesByTip.has(v.tip_id)) votesByTip.set(v.tip_id, []);
        votesByTip.get(v.tip_id).push(v);
    });

    const enriched = tips.map((t) => {
        const all = votesByTip.get(t.id) || [];
        const myVote = all.find((v) => v.user_id === user.id);
        const starVotes = all.filter((v) => !v.ban && v.stars);
        const banVotes  = all.filter((v) => v.ban);
        const voterDetails = all
            .map((v) => ({ name: profilesMap.get(v.user_id) || '?', stars: v.ban ? null : v.stars, ban: !!v.ban }))
            .sort((a, b) => (a.ban ? 1 : 0) - (b.ban ? 1 : 0)
                || ((b.stars || 0) - (a.stars || 0))
                || a.name.localeCompare(b.name, 'cs'));
        return {
            ...t,
            state:       tipStatus(t),
            myStars:     (myVote && !myVote.ban) ? myVote.stars : 0,
            myBan:       !!(myVote && myVote.ban),
            totalStars:  starVotes.reduce((s, v) => s + v.stars, 0),
            voterCount:  starVotes.length,
            banCount:    banVotes.length,
            voterDetails,
            creatorName:  profilesMap.get(t.created_by)  || '—',
            archiverName: profilesMap.get(t.archived_by) || null
        };
    });

    const active   = enriched.filter((t) => t.state === ACTIVE);
    const wip      = enriched.filter((t) => t.state === WIP);
    const rejected = enriched.filter((t) => t.state === REJECTED);

    // Řazení aktivních: zabanované dolů; pak nejvíce hvězdiček nahoře, počet hlasujících, abecedně
    active.sort((a, b) =>
        ((a.banCount > 0 ? 1 : 0) - (b.banCount > 0 ? 1 : 0)) ||
        (b.totalStars - a.totalStars) ||
        (b.voterCount - a.voterCount) ||
        a.title.localeCompare(b.title, 'cs')
    );
    // Archiv: naposledy archivované nahoře
    const archStamp = (t) => {
        const d = new Date(t.archived_at || t.created_at || 0).getTime();
        return Number.isNaN(d) ? 0 : d;
    };
    wip.sort((a, b) => archStamp(b) - archStamp(a));
    rejected.sort((a, b) => archStamp(b) - archStamp(a));

    const archivedCount = wip.length + rejected.length;
    // Jen hvězdičkové hlasy — ať hlavička sedí se součty v řádcích (bany se počítají zvlášť)
    const activeVotes = active.reduce((s, t) => s + t.voterCount, 0);

    container.innerHTML = `
        <div class="filters">
            <h2 style="font-size:18px; margin-right:auto;">Nové tipy</h2>
            <span style="font-size:13px;color:var(--text-muted);margin-right:12px;">
                ${active.length} ${plural(active.length, 'tip', 'tipy', 'tipů')} ·
                ${activeVotes} ${voterWord(activeVotes)}${archivedCount > 0 ? ` · ${archivedCount} v archivu` : ''}
            </span>
            <button class="btn btn-sm btn-primary" id="add-tip-btn">+ Nový tip</button>
        </div>

        <div class="tips-list">
            ${active.length === 0
                ? (archivedCount > 0
                    ? '<div class="state-banner">Žádné aktivní tipy — všechny jsou v archivu níže.</div>'
                    : '<div class="state-banner">Žádné tipy zatím. Klikni <strong>+ Nový tip</strong> a přidej píseň, kterou bys chtěl zařadit.</div>')
                : active.map((t) => renderTipCard(t, admin)).join('')}
        </div>

        ${renderArchiveSection(WIP, 'Na zkoušku — pracuje se na zařazení do repertoáru', wip, admin)}
        ${renderArchiveSection(REJECTED, 'Zamítnuté', rejected, admin)}

        <div class="footer-note">
            <strong>Jak to funguje:</strong> klikni na hvězdičku 1 (nehrát) až 5 (určitě hrát) — tvůj hlas se uloží.
            Tlačítkem <strong>🚫</strong> dáš najevo, že tuto píseň hrát nechceš — tip pak zčervená.
            Tlačítkem <strong>✏ Upravit</strong> (nebo dvojklikem na název) tip upravíš — kdokoli může opravit název,
            doplnit YouTube odkaz i poznámku.
            Najetím myší na souhrn uvidíš, kdo hlasoval. Tipy s nejvyšším součtem hvězdiček jsou nahoře, zabanované dole.
            ${admin ? `Jako správce máš navíc <strong>⇥ Archiv</strong> — přesun mezi „na zkoušku" nebo zamítnuté.
            Píseň poslaná na zkoušku se zároveň objeví jako bod na <strong>Zkoušce</strong>; při odchodu z ní se
            nesplněný bod ze Zkoušky zase odebere (pokud ho někdo nerozepsal — ten zůstane). Hlasy zůstávají
            uložené, tip se nemaže. Archivovaný tip smíš upravovat a mazat jen ty — ostatním se u něj tlačítka nenabízejí.`
            : 'Archivované tipy dole jsou jen ke čtení — upravit nebo vrátit je může správce.'}
        </div>
    `;

    const reload = () => renderTipy(container);

    // Zapamatuj si rozbalené sekce archivu, ať se po každé akci nesbalí
    container.querySelectorAll('details.tips-archive').forEach((d) => {
        d.addEventListener('toggle', () => {
            if (d.open) openArchive.add(d.dataset.section);
            else openArchive.delete(d.dataset.section);
        });
    });

    container.querySelector('#add-tip-btn').addEventListener('click', () => {
        openTipModal({ onSaved: reload });
    });

    container.querySelectorAll('[data-set-stars]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const tipId = btn.dataset.tipId;
            const stars = parseInt(btn.dataset.setStars, 10);
            const currentMy = parseInt(btn.dataset.myCurrent || '0', 10);
            // Klik na stejnou hvězdičku jako moje aktuální = zrušení hlasu
            if (currentMy === stars) await deleteVote(tipId, user.id);
            else                     await setVote(tipId, user.id, { stars, ban: false });
            await reload();
        });
    });

    container.querySelectorAll('[data-ban-tip]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const tipId = btn.dataset.banTip;
            const hadBan = btn.dataset.myBan === '1';
            if (hadBan) await deleteVote(tipId, user.id);                       // zrušit ban
            else        await setVote(tipId, user.id, { stars: null, ban: true }); // zabanovat
            await reload();
        });
    });

    container.querySelectorAll('[data-admin-clearban]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const tipId = btn.dataset.adminClearban;
            if (!confirm('Zrušit ban u tohoto tipu? Odstraní se ban hlasy od všech, kdo ho dali.')) return;
            const { error } = await supabase.from('song_tip_votes')
                .delete().eq('tip_id', tipId).eq('ban', true);
            if (error) { alert('Zrušení banu selhalo: ' + error.message); return; }
            await reload();
        });
    });

    // Editace: tlačítko ✏ (všichni přihlášení) i dvojklik na název — beze změny zvyklostí.
    // Archivovaný tip smí smazat jen správce (politika tips_delete_active_or_admin),
    // tak tam tlačítko „Smazat" ostatním ani nenabízíme.
    const openEditFor = (id) => {
        const tip = enriched.find((t) => t.id === id);
        if (!tip) return;
        openTipModal({ tip, canDelete: tip.state === ACTIVE || admin, onSaved: reload });
    };
    container.querySelectorAll('[data-edit-btn]').forEach((btn) => {
        btn.addEventListener('click', () => openEditFor(btn.dataset.editBtn));
    });
    container.querySelectorAll('[data-edit-tip]').forEach((el) => {
        el.addEventListener('dblclick', (e) => {
            e.preventDefault();
            window.getSelection?.()?.removeAllRanges();
            openEditFor(el.dataset.editTip);
        });
    });

    // Archiv (jen správce)
    container.querySelectorAll('[data-archive-tip]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const tip = enriched.find((t) => t.id === btn.dataset.archiveTip);
            if (tip) openArchiveModal({ tip, onDone: reload });
        });
    });

    container.querySelectorAll('[data-unarchive-tip]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const tip = enriched.find((t) => t.id === btn.dataset.unarchiveTip);
            if (!tip) return;
            if (!confirm(`Vrátit „${tip.title}" zpět mezi aktivní tipy? Hlasy zůstávají zachované.`)) return;
            btn.disabled = true;
            const { error } = await setTipStatus(tip.id, ACTIVE);
            if (error) {
                btn.disabled = false;
                alert('Vrácení z archivu selhalo: ' + error.message + migrationHint(error));
                return;
            }
            if (tip.state === WIP) await clearRehearsalItem(tip);
            await reload();
        });
    });
}

function renderArchiveSection(key, heading, list, admin) {
    if (!list.length) return '';
    return `
        <details class="tips-archive" data-section="${key}"${openArchive.has(key) ? ' open' : ''}>
            <summary>${esc(heading)} (${list.length})</summary>
            <div class="tips-list tips-list-archive">
                ${list.map((t) => renderArchivedCard(t, admin)).join('')}
            </div>
        </details>
    `;
}

function ytLink(t) {
    const url = safeUrl(t.youtube_url);
    return url
        ? `<a class="tip-yt" href="${esc(url)}" target="_blank" rel="noopener noreferrer" title="Otevřít: ${esc(url)}">▶ YT</a>`
        : '';
}

// Popisek stavu v archivu — u neznámé hodnoty ukáže syrový stav, ať je to vidět
function archBadge(t) {
    if (t.status && t.status !== ACTIVE && !STATUS_LABEL[t.status]) return t.status;
    return STATUS_LABEL[t.state] || t.state;
}

function voterTooltip(t) {
    return t.voterDetails.length
        ? t.voterDetails.map((v) => v.ban ? `${v.name} BAN` : `${v.name} ${v.stars}★`).join(', ')
        : 'Zatím nikdo nehlasoval';
}

// Spodní řádek: poznámka vlevo (ořízne se), kdo vložil a kdy vpravo (vždy vidět)
function metaLine(t, extra = '') {
    const meta = [esc(t.creatorName), fmtDate(t.created_at)].filter(Boolean);
    if (extra) meta.push(esc(extra));
    const note = t.notes ? esc(t.notes) : '';
    return `
        <div class="tip-line2">
            <span class="tip-note"${note ? ` title="${note}"` : ''}>${note}</span>
            <span class="tip-by">${meta.join(' · ')}</span>
        </div>
    `;
}

function renderTipCard(t, admin) {
    const stars = [1, 2, 3, 4, 5].map((s) => `
        <button class="tip-star${t.myStars >= s ? ' on' : ''}"
                data-set-stars="${s}"
                data-tip-id="${t.id}"
                data-my-current="${t.myStars}"
                aria-label="${s} z 5"
                title="${s} hvězdiček${s === 1 ? ' (nehrát)' : (s === 5 ? ' (určitě hrát)' : '')}">★</button>
    `).join('');
    const banBtn = `<button class="tip-ban${t.myBan ? ' on' : ''}"
        data-ban-tip="${t.id}" data-my-ban="${t.myBan ? 1 : 0}"
        aria-label="Ban" title="Ban — tuto píseň hrát nechci">🚫</button>`;

    return `
        <div class="tip-row tip-compact${t.banCount > 0 ? ' tip-banned' : ''}">
            <div class="tip-line1">
                <div class="tip-title" data-edit-tip="${t.id}" title="Dvojklik = upravit">${esc(t.title)}</div>
                ${ytLink(t)}
                <button class="tip-icon-btn" data-edit-btn="${t.id}"
                        aria-label="Upravit tip" title="Upravit tip (název, YouTube, poznámka)">✏</button>
                ${admin ? `<button class="tip-icon-btn" data-archive-tip="${t.id}"
                        aria-label="Přesunout do archivu" title="Přesunout do archivu (jen správce)">⇥</button>` : ''}
                <div class="tip-voteline">
                    <div class="tip-stars">${stars}${banBtn}</div>
                    <div class="tip-totals" title="${esc(voterTooltip(t))}">
                        <span class="tip-total-num">★${t.totalStars}</span>
                        <span class="tip-voter-count">${t.voterCount} ${voterWord(t.voterCount)}${t.banCount > 0 ? ` · 🚫 ${t.banCount}` : ''}</span>
                    </div>
                </div>
            </div>
            ${metaLine(t)}
            ${(admin && t.banCount > 0) ? `
            <div class="tip-admin-row">
                <button class="tip-clearban-btn" data-admin-clearban="${t.id}">✕ Zrušit ban (${t.banCount})</button>
            </div>` : ''}
        </div>
    `;
}

function renderArchivedCard(t, admin) {
    const archInfo = t.archived_at
        ? `archivováno ${fmtDate(t.archived_at)}${t.archiverName ? ` (${t.archiverName})` : ''}`
        : '';
    return `
        <div class="tip-row tip-compact tip-archived tip-arch-${esc(t.state)}">
            <div class="tip-line1">
                <div class="tip-title"${admin ? ` data-edit-tip="${t.id}" title="Dvojklik = upravit"` : ''}>${esc(t.title)}</div>
                ${ytLink(t)}
                ${admin ? `
                <button class="tip-icon-btn" data-edit-btn="${t.id}"
                        aria-label="Upravit tip" title="Upravit tip (název, YouTube, poznámka)">✏</button>
                <button class="tip-icon-btn" data-archive-tip="${t.id}"
                        aria-label="Změnit stav v archivu" title="Změnit stav v archivu (jen správce)">⇥</button>
                <button class="tip-icon-btn" data-unarchive-tip="${t.id}"
                        aria-label="Vrátit mezi aktivní tipy" title="Vrátit mezi aktivní tipy (jen správce)">↩</button>` : ''}
                <div class="tip-voteline">
                    <span class="tip-arch-badge">${esc(archBadge(t))}</span>
                    <div class="tip-totals" title="${esc(voterTooltip(t))}">
                        <span class="tip-total-num">★${t.totalStars}</span>
                        <span class="tip-voter-count">${t.voterCount} ${voterWord(t.voterCount)}${t.banCount > 0 ? ` · 🚫 ${t.banCount}` : ''}</span>
                    </div>
                </div>
            </div>
            ${metaLine(t, archInfo)}
        </div>
    `;
}

async function setVote(tipId, userId, { stars = null, ban = false }) {
    const { error } = await supabase.from('song_tip_votes').upsert({
        tip_id: tipId, user_id: userId,
        stars: ban ? null : stars,
        ban: !!ban,
        voted_at: new Date().toISOString()
    }, { onConflict: 'tip_id,user_id' });
    if (error) alert('Hlasování selhalo: ' + error.message);
}

async function deleteVote(tipId, userId) {
    const { error } = await supabase.from('song_tip_votes')
        .delete().eq('tip_id', tipId).eq('user_id', userId);
    if (error) alert('Zrušení hlasu selhalo: ' + error.message);
}

/**
 * Změna stavu tipu. archived_at/archived_by dopisuje DB trigger, klient je neposílá.
 * Vracíme chybu i když se nezměnil žádný řádek — jinak by UI hlásilo úspěch
 * u tipu, který mezitím někdo smazal.
 */
async function setTipStatus(tipId, status) {
    const { data, error } = await supabase.from('song_tips')
        .update({ status }).eq('id', tipId).select('id');
    if (error) return { error };
    if (!data || data.length !== 1) {
        return { error: { message: 'Tip se nepodařilo změnit — možná ho mezitím někdo smazal nebo na to nemáš právo. Obnov stránku.' } };
    }
    return {};
}

/**
 * Bod na Zkoušku pro píseň poslanou na zkoušku. Duplicitu hlídá vazba tip_id
 * (+ unikátní index v DB), takže opakované archivování bod nezdvojí.
 * Když už bod existuje jako odzkoušený, vrátíme ho mezi nesplněné.
 */
async function ensureRehearsalItem(tip) {
    const { data: existing, error: selErr } = await supabase
        .from('rehearsal_items').select('id, done').eq('tip_id', tip.id).limit(1);
    if (selErr) return { error: selErr };

    if (existing && existing.length) {
        if (!existing[0].done) return { skipped: true };
        const { error } = await supabase.from('rehearsal_items')
            .update({ done: false }).eq('id', existing[0].id);
        return { error };
    }

    const { data: { user } } = await supabase.auth.getUser();
    // Do poznámky jde JEN text z tipu. Odkaz na YouTube se sem nepíše — ve
    // Zkoušce ho ukazuje ikona ▶ YT (bere ho z navázaného tipu), takže v textu
    // by byl zdvojený. Ani prefix „z Nových tipů" ne — původ ukazuje rozklik.
    const { error } = await supabase.from('rehearsal_items').insert({
        song_title: tip.title,
        note: tip.notes || null,
        tip_id: tip.id,
        created_by: user?.id
    });
    // Souběžné archivování dvěma správci: unikátní index odmítne druhý insert,
    // ale bod existuje — to je úspěch, ne chyba.
    if (error && error.code === '23505') return { skipped: true };
    return { error };
}

/**
 * Odchod ze stavu „na zkoušku" – nesplněný bod na Zkoušce už nedává smysl.
 * Odzkoušené body (done = true) zůstávají jako historie.
 * Bod, který mezitím někdo na Zkoušce rozepsal, NEMAŽEME — jen ho odpojíme
 * od tipu, ať se nikomu neztratí jeho poznámky.
 */
// Starší automaticky založené body měly tenhle prefix v poznámce; dnes už se
// nepíše, ale při rozpoznávání „nedotčeného" bodu ho musíme dál tolerovat.
const LEGACY_NOTE_PREFIX = 'z Nových tipů';

// Bod je nedotčený, když ho nikdo nepřepsal — název i poznámka sedí s tipem.
function isPristine(item, tip) {
    if (item.song_title !== tip.title) return false;
    const note = (item.note || '').trim();
    if (note.startsWith(LEGACY_NOTE_PREFIX)) return true;   // bod z dřívější verze
    return note === (tip.notes || '').trim();
}

async function clearRehearsalItem(tip) {
    const { data, error: selErr } = await supabase.from('rehearsal_items')
        .select('id, note, song_title, done').eq('tip_id', tip.id).eq('done', false);
    if (selErr) { console.warn('Úklid bodu na Zkoušce selhal:', selErr.message); return { error: selErr }; }
    if (!data || !data.length) return {};

    const pristine = data.filter((i) => isPristine(i, tip));
    const edited = data.filter((i) => !pristine.includes(i));

    if (pristine.length) {
        const { error } = await supabase.from('rehearsal_items')
            .delete().in('id', pristine.map((i) => i.id));
        if (error) console.warn('Smazání bodu na Zkoušce selhalo:', error.message);
    }
    if (edited.length) {
        const { error } = await supabase.from('rehearsal_items')
            .update({ tip_id: null }).in('id', edited.map((i) => i.id));
        if (error) console.warn('Odpojení bodu na Zkoušce selhalo:', error.message);
    }
    return { keptEdited: edited.length };
}

// Odstraní starý modál i s jeho Escape listenerem (aby se nehromadily na document)
function dropModal(id) {
    const old = document.getElementById(id);
    if (!old) return;
    if (old._onEsc) document.removeEventListener('keydown', old._onEsc);
    old.remove();
}

function openArchiveModal({ tip, onDone } = {}) {
    dropModal('tip-archive-modal');
    const m = document.createElement('div');
    m.id = 'tip-archive-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window" style="max-width: 460px;">
            <div class="modal-head">
                <h2>Přesunout do archivu</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <div class="modal-body">
                <p style="margin:0 0 14px;font-size:14px;">
                    Tip <strong>${esc(tip.title)}</strong> — kam ho zařadit?
                    Tip ani hlasy se nemažou, zůstanou dohledatelné v archivu.
                    ${tip.state === WIP
                        ? 'Odchodem ze stavu „na zkoušku" zmizí i automatický bod na Zkoušce (ručně rozepsaný bod zůstane).'
                        : ''}
                </p>
                <button class="btn tip-arch-choice" type="button" id="ta-wip"
                        ${tip.state === WIP ? 'disabled data-locked="1"' : ''}>
                    🎵 Na zkoušku
                    <small>Pracuje se na zařazení do repertoáru. Zároveň se založí bod na Zkoušce.</small>
                </button>
                <button class="btn tip-arch-choice" type="button" id="ta-rejected"
                        ${tip.state === REJECTED ? 'disabled data-locked="1"' : ''}>
                    🚫 Zamítnutá
                    <small>Píseň se hrát nebude. Zůstane dohledatelná v archivu.</small>
                </button>
                <p id="ta-error" style="color:var(--accent); font-size:13px; margin-top:10px; display:none;"></p>
            </div>
            <div class="modal-foot">
                <button class="btn" type="button" id="ta-cancel">Zrušit</button>
            </div>
        </div>
    `;
    document.body.appendChild(m);

    const onEsc = (e) => { if (e.key === 'Escape') close(); };
    m._onEsc = onEsc;
    function close() {
        document.removeEventListener('keydown', onEsc);
        m.remove();
    }
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#ta-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });
    document.addEventListener('keydown', onEsc);

    // Chyba se musí dostat k uživateli i když mezitím modál zavřel
    const fail = (msg) => {
        if (!m.isConnected) { alert(msg); return; }
        const el = m.querySelector('#ta-error');
        el.textContent = msg;
        el.style.display = 'block';
        m.querySelectorAll('.tip-arch-choice').forEach((b) => {
            b.disabled = b.dataset.locked === '1';
        });
    };

    const run = async (status) => {
        m.querySelectorAll('.tip-arch-choice').forEach((b) => { b.disabled = true; });
        const { error } = await setTipStatus(tip.id, status);
        if (error) { fail('Archivace selhala: ' + error.message + migrationHint(error)); return; }

        let warn = '';
        if (status === WIP) {
            // Stav už je uložený, případný neúspěch jen ohlásíme.
            const r = await ensureRehearsalItem(tip);
            if (r.error) {
                warn = 'Tip je archivovaný jako „na zkoušku", ale bod na Zkoušku se nepodařilo založit: '
                    + r.error.message + migrationHint(r.error);
            }
        } else if (tip.state === WIP) {
            await clearRehearsalItem(tip); // odchod ze stavu „na zkoušku" uklidí bod
        }

        close();
        if (warn) alert(warn);
        onDone?.();
    };

    m.querySelector('#ta-wip').addEventListener('click', () => run(WIP));
    m.querySelector('#ta-rejected').addEventListener('click', () => run(REJECTED));
}

function openTipModal({ tip = null, canDelete = true, onSaved } = {}) {
    const isEdit = !!tip;
    dropModal('tip-modal');
    const m = document.createElement('div');
    m.id = 'tip-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window" style="max-width: 480px;">
            <div class="modal-head">
                <h2>${isEdit ? 'Upravit tip' : 'Nový tip'}</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="at-form">
                <div class="modal-body">
                    <label class="se-field">
                        <span>Název písně</span>
                        <input type="text" name="title" required autofocus
                               placeholder="Název písničky"
                               value="${esc(tip?.title || '')}">
                    </label>
                    <label class="se-field" style="margin-top:12px;">
                        <span>Odkaz na YouTube (nepovinné)</span>
                        <input type="url" name="youtube_url"
                               placeholder="https://youtube.com/..."
                               value="${esc(tip?.youtube_url || '')}">
                    </label>
                    <label class="se-field" style="margin-top:12px;">
                        <span>Poznámka (autor, kde jsi to slyšel, kdo to má zpívat nebo hrát — nepovinné)</span>
                        <textarea name="notes" rows="2">${esc(tip?.notes || '')}</textarea>
                    </label>
                    <p id="at-error" style="color:var(--accent); font-size:13px; margin-top:10px; display:none;"></p>
                </div>
                <div class="modal-foot">
                    ${(isEdit && canDelete) ? '<button class="btn rep-btn-danger" type="button" id="at-delete" style="margin-right:auto;">🗑 Smazat tip</button>' : ''}
                    <button class="btn" type="button" id="at-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">${isEdit ? 'Uložit změny' : 'Přidat'}</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    const onEsc = (e) => { if (e.key === 'Escape') close(); };
    m._onEsc = onEsc;
    function close() {
        document.removeEventListener('keydown', onEsc);
        m.remove();
    }
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#at-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });
    document.addEventListener('keydown', onEsc);

    const showErr = (msg) => {
        if (!m.isConnected) { alert(msg); return; }
        const errEl = m.querySelector('#at-error');
        errEl.textContent = msg;
        errEl.style.display = 'block';
    };

    const delBtn = m.querySelector('#at-delete');
    if (delBtn) delBtn.addEventListener('click', async () => {
        if (!confirm(`Smazat tip „${tip.title}"? Tato akce je nevratná.`)) return;
        // .select() je nutné: bez něj vrací PostgREST 204 i když řádek odfiltruje
        // RLS (např. tip mezitím někdo archivoval) → UI by hlásilo falešný úspěch.
        const { data, error } = await supabase.from('song_tips')
            .delete().eq('id', tip.id).select('id');
        if (error) { showErr('Smazání selhalo: ' + error.message); return; }
        if (!data || data.length !== 1) {
            showErr('Tip se nepodařilo smazat — možná ho mezitím někdo archivoval (archivovaný tip smí mazat jen správce). Obnov stránku.');
            return;
        }
        close();
        onSaved?.();
    });

    m.querySelector('#at-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const errEl = m.querySelector('#at-error');
        errEl.style.display = 'none';

        const fd = new FormData(e.target);
        const title = fd.get('title').trim();
        const notes = fd.get('notes').trim();
        const youtube = fd.get('youtube_url').trim();
        if (!title) { showErr('Název je povinný.'); return; }
        if (youtube && !safeUrl(youtube)) {
            showErr('Odkaz musí začínat http:// nebo https:// — jiné adresy nejsou povolené.');
            return;
        }

        let error;
        if (isEdit) {
            const r = await supabase.from('song_tips').update({
                title,
                notes:       notes   || null,
                youtube_url: youtube || null
            }).eq('id', tip.id);
            error = r.error;
        } else {
            const { data: { user } } = await supabase.auth.getUser();
            const r = await supabase.from('song_tips').insert({
                title,
                notes:       notes   || null,
                youtube_url: youtube || null,
                created_by:  user?.id
            });
            error = r.error;
        }

        if (error) { showErr('Uložení selhalo: ' + error.message); return; }
        close();
        onSaved?.();
    });
}
